import { useState, useEffect, useRef, useMemo } from 'react';
import { getRankRingParams } from './utils/rankUtils';
import RankBadge from './RankBadge';
import { AnimatePresence, motion, useMotionValue, useTransform, PanInfo } from 'motion/react';
import { X, UserPlus, MapPin, Briefcase, GraduationCap, Search, SlidersHorizontal, Bell, Sparkles, Users, MessageSquare, HandMetal, Zap } from 'lucide-react';
import { useMetricDelta } from './MetricDeltaContext';
import { SkeletonCard } from './ui/SkeletonPrimitives';
import logoImage from '../assets/f736bb54a1ccb5a9a30d8b8ec9d3974d4b7ef9d6.png';
import logoImageWhite from '../assets/fac7d53f9fcb6f491ee2fb6641c18821c53c606e.png';
import goldIcon from '../assets/gold icon.png';
import moreIcon from '../assets/MORE.png';
import swipeIcon from '../assets/swipe.png';
import tapIcon from '../assets/tap.png';
import swipeBlack from '../assets/swipe-black.png';
import tapBlack from '../assets/tap black.png';
import { UserDetailModal } from './UserDetailModal';
import { ActivityStatus } from './ui/ActivityStatus';
import { RecentlyActiveBadge } from './ui/RecentlyActiveBadge';
import { FormattedText } from './FormattedText';
import { ProfilePictureModal } from './ProfilePictureModal';
import { DiscoverFilterModal, FilterOptions } from './DiscoverFilterModal';
import { MatchPopup } from './MatchPopup';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { DiscoveryPaginationDots } from './DiscoveryPaginationDots';
import { useUserData } from './UserDataContext';
import { useNotificationBadges } from './NotificationContext';
import { FlagImage, getEmojiFlag, countryToFlagCode } from './utils/countryFlags';
import { getFlagImage } from '../assets/flags';
import { UserService, SearchService, EconomyService } from '../services/api';
import { DiscoverService, type SwipeMode } from '../services/DiscoverService';
import { SWIPE_COSTS } from '../constants/economy';
import { type User } from '../types';
import { ParallaxBackground } from './utils/parallax';
import { useTheme } from './ThemeProvider';
import { calculateAge } from './utils/timeFormat';
import { formatCount } from '../utils/numberFormat';
import { SafeStorage } from '../utils/safeStorage';
import { scaleSize } from '../utils/scaling';

import { InsufficientFundsModal } from './InsufficientFundsModal';
import { identityData, identityCategories, categoryStyles, getCategoryByTag } from '../constants/identities';
import { IdentityTag } from './IdentityTag';
import { SCREENS, EVENTS } from '../constants/navigation';

import { memo, forwardRef, useImperativeHandle } from 'react';
import { useScreenReady } from '../contexts/ScreenTransitionContext';
import { ScreenLoader } from './ScreenLoader';
import { animate } from 'motion/react';
import { Capacitor } from '@capacitor/core';
import { getUserAvatar } from '../utils/userUtils';

interface DiscoverScreenProps {
  onMatch: (user: any) => void;
  initialCountry?: string;
  /** When V1 is shown as overlay from V2, tapping the Zap button in the action bar calls this to return to V2. */
  onReturnToV2?: () => void;
}

// 🔒 ISOLATED COMPONENT: Solves shared-state race conditions
interface SwipeableCardProps {
  user: any;
  index: number;
  totalUsers: number;
  onSwipe: (direction: 'left' | 'right') => void;
  onPress: () => void; // Image click (opens picture viewer)
  onSeeMore: () => void; // MORE button (opens user detail only)
  theme: string;
  formattedAge: string | number; // Pass pre-calculated age
}

export interface SwipeableCardHandle {
  triggerSwipe: (direction: 'left' | 'right') => Promise<void>;
}

const SwipeableCard = memo(forwardRef<SwipeableCardHandle, SwipeableCardProps>(({ 
  user, 
  index, 
  totalUsers, 
  onSwipe, 
  onPress,
  onSeeMore,
  theme,
  formattedAge
}, ref) => {
  const { getOptimisticCount } = useMetricDelta();
  const x = useMotionValue(0);
  const rotate = useTransform(x, [-200, 200], [-30, 30]);
  const opacity = useTransform(x, [-150, 0, 150], [0.5, 1, 0.5]);
  const cardRef = useRef<HTMLDivElement>(null);
  const [cardImageIndex, setCardImageIndex] = useState(0);
  // Track which image slots have fully loaded — stored in a ref to avoid triggering re-renders
  // during the drag animation. We use a state only to schedule the actual opacity transition.
  const loadedSlotsRef = useRef<Set<number>>(new Set());
  const [loadedSlots, setLoadedSlots] = useState<Set<number>>(new Set());

  const markSlotLoaded = (slot: number) => {
    if (!loadedSlotsRef.current.has(slot)) {
      loadedSlotsRef.current.add(slot);
      // Batch the state update so it only re-renders once the image is truly ready
      setLoadedSlots(new Set(loadedSlotsRef.current));
    }
  };

  useImperativeHandle(ref, () => ({
    triggerSwipe: async (direction) => {
      // Programmatic swipe (from buttons)
      await animate(x, direction === 'right' ? 1000 : -1000, { duration: 0.3 }).then(() => {
        // Animation done
      });
    }
  }));

  const handleDragEnd = (_: any, info: PanInfo) => {
    const velocityThreshold = 200;
    const direction = info.offset.x > 0 || info.velocity.x > velocityThreshold ? 1 : -1;
    const isSwipe = Math.abs(info.offset.x) > 100 || Math.abs(info.velocity.x) > velocityThreshold;

    if (isSwipe) {
      const targetX = direction === 1 ? 1000 : -1000;
      animate(x, targetX, { duration: 0.2 }).then(() => {
        onSwipe(direction === 1 ? 'right' : 'left');
      });
    } else {
      animate(x, 0, { type: 'spring', stiffness: 300, damping: 20 });
    }
  };

  const isMaybeVideo = (url?: string) => {
    if (!url) return false;
    const clean = url.toLowerCase().split('?')[0];
    return clean.endsWith('.mp4') || clean.endsWith('.mov') || clean.endsWith('.webm') || url.includes('video');
  };

  // Pre-compute media items once (no hot-path computation inside JSX)
  const mediaItems: Array<{ url: string, videoUrl: string, slot: number }> = [];
  for (let i = 0; i < 4; i++) {
     const preview = (user.portfolioPreviews?.[i] || '').trim();
     const image = (user.portfolio?.[i] || '').trim();
     const video = (user.portfolioVideos?.[i] || '').trim();
     
     if (preview || image || video) {
         const backImg = isMaybeVideo(preview) ? "" : (preview || (isMaybeVideo(image) ? "" : (image || user.image || user.avatar || '')));
         mediaItems.push({
             url: backImg || user.image || user.avatar || '',
             videoUrl: video || (isMaybeVideo(preview) ? preview : (isMaybeVideo(image) ? image : '')),
             slot: i
         });
     }
  }
  if (mediaItems.length === 0) {
     mediaItems.push({ url: user.image || user.avatar || '', videoUrl: '', slot: -1 });
  }
  
  const images = mediaItems.map(m => m.url);

  // 🔒 PERF FIX #2: Pre-compute trim metadata as a Map so onTimeUpdate never
  // runs a linear .find() scan at 60Hz. Built once per user change.
  const trimMetaMap = useMemo(() => {
    const map = new Map<number, { trimStart: number; trimEnd: number }>();
    if (Array.isArray(user.photoMetadata)) {
      user.photoMetadata.forEach((m: any) => {
        if (m?.slot !== undefined && m?.trimStart !== undefined && m?.trimEnd !== undefined) {
          map.set(Number(m.slot), { trimStart: Number(m.trimStart), trimEnd: Number(m.trimEnd) });
        }
      });
    }
    return map;
  }, [user.photoMetadata]);

  // 🔒 PERF FIX #3: Removed pre-compute in favor of FlagImage component which handles resolution robustly

  return (
    <motion.div
      ref={cardRef}
      className="absolute inset-0 shadow-2xl card-shadow"
      style={{
        x: index === 0 ? x : 0,
        rotate: index === 0 ? rotate : 0,
        opacity: index === 0 ? opacity : 0.5,
        scale: index === 0 ? 1 : 0.95,
        zIndex: 10 + (totalUsers - index),
        touchAction: 'none',
        borderRadius: '1.5rem',
        willChange: 'transform, opacity, rotate',
        transform: index === 0 ? `translate3d(${x.get()}px, 0, 0) rotate(${rotate.get()}deg)` : 'translate3d(0,0,0)',
        backfaceVisibility: 'hidden',
        WebkitBackfaceVisibility: 'hidden',
        overflow: 'visible' // Allow flag badge to escape card bounds
      }}
      drag={index === 0 ? 'x' : false}
      dragConstraints={{ left: 0, right: 0 }}
      onDragEnd={index === 0 ? handleDragEnd : undefined}
      dragElastic={0.7}
      whileTap={{ cursor: 'grabbing' }}
    >
      <div className="relative w-full h-full rounded-3xl z-1" style={{ overflow: 'visible' }}>
        <div className="relative w-full h-full bg-[#1a1a1a] rounded-3xl overflow-hidden">
          {/* Use a stack and opacity for instantaneous shifting */}
          {mediaItems.map((item, i) => {
            const isActive = i === cardImageIndex;
            const videoUrl = item.videoUrl;
            const slot = item.slot;
            
            return (
              <div key={i} className="absolute inset-0">
                {/* ── Placeholder layer: blurred low-res thumbnail shown instantly ── */}
                {!loadedSlots.has(i) && (
                  item.url ? (
                    // Blurred version of the same URL — browser likely already has it cached
                    // from the preview or a previously-seen card. Shows instantly.
                    <img
                      src={item.url}
                      alt=""
                      aria-hidden
                      className="absolute inset-0 w-full h-full object-cover select-none pointer-events-none"
                      style={{
                        filter: 'blur(12px)',
                        transform: 'scale(1.08)', // slightly overscale so blur edges don't show
                        zIndex: 0,
                        opacity: isActive ? 1 : 0,
                        transition: 'opacity 0.2s',
                      }}
                      draggable={false}
                    />
                  ) : (
                    // Shimmer skeleton if no URL at all
                    <div
                      className="absolute inset-0"
                      style={{
                        zIndex: 0,
                        background: 'linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 50%, #1a1a1a 100%)',
                        backgroundSize: '200% 200%',
                        animation: 'shimmer 1.5s infinite',
                        opacity: isActive ? 1 : 0,
                      }}
                    />
                  )
                )}

                {/* ── Full-res image: fades in once loaded ── */}
                <motion.img
                  src={item.url}
                  alt={`${user.name}-${i}`}
                  initial={false}
                  animate={{ 
                    opacity: isActive ? (loadedSlots.has(i) ? 1 : 0) : 0,
                    scale: isActive ? 1 : 1.05
                  }}
                  transition={{ duration: 0.25, ease: 'easeOut' }}
                  className="absolute inset-0 w-full h-full object-cover select-none"
                  style={{ 
                    backfaceVisibility: 'hidden', 
                    WebkitBackfaceVisibility: 'hidden',
                    zIndex: 1,
                    willChange: 'opacity, transform',
                    pointerEvents: 'none'
                  }}
                  draggable={false}
                  onLoad={() => markSlotLoaded(i)}
                />
                {videoUrl && isActive && (
                  <motion.video
                    key={`${videoUrl}-${i}`}
                    src={videoUrl}
                    autoPlay
                    muted
                    loop
                    playsInline
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.4 }}
                    className="absolute inset-0 w-full h-full object-cover select-none"
                    style={{ 
                      backfaceVisibility: 'hidden', 
                      WebkitBackfaceVisibility: 'hidden',
                      zIndex: 2,
                      pointerEvents: 'none'
                    }}
                    ref={(node) => {
                      if (node) {
                        node.play().catch(() => console.warn('[Discover] autoPlay blocked'));
                      }
                    }}
                    onLoadedData={(e) => {
                      const meta = trimMetaMap.get(slot);
                      if (meta?.trimStart !== undefined) {
                        e.currentTarget.currentTime = meta.trimStart;
                      }
                      e.currentTarget.play().catch(() => {});
                    }}
                    onTimeUpdate={(e) => {
                      const meta = trimMetaMap.get(slot);
                      if (meta && meta.trimEnd > 0) {
                        const vid = e.currentTarget;
                        if (vid.currentTime >= meta.trimEnd || vid.currentTime < meta.trimStart - 0.5) {
                          vid.currentTime = meta.trimStart;
                        }
                      }
                    }}
                  />
                )}
              </div>
            );
          })}

          {/* Vignette - backdrop-blur forces GPU compositing layer that renders above image layers (same as forums) */}
          <div 
            className="absolute inset-0 pointer-events-none backdrop-blur-[2px]"
            style={{ 
              zIndex: 10,
              background: 'radial-gradient(ellipse at center, rgba(0,0,0,0.2) 20%, rgba(0,0,0,0.85) 100%)'
            }}
          />
        </div>

        {/* Tap Interaction Layer */}
        <div 
          className="absolute inset-0 z-40 cursor-pointer pointer-events-auto rounded-3xl"
          onClick={(e) => {
            const rect = e.currentTarget.getBoundingClientRect();
            const tapX = e.clientX - rect.left;
            if (images.length > 1) {
              // 📐 30/70 SPLIT: Favor forward navigation
              if (tapX < rect.width * 0.3) {
                setCardImageIndex((prev) => (prev - 1 + images.length) % images.length);
              } else {
                setCardImageIndex((prev) => (prev + 1) % images.length);
              }
            }
            // If images.length === 1, tap does nothing. We deliberately do not call onPress()
            // here because tapping the card should only cycle, not open the full-screen modal.
          }}
        />



          {/* Bottom Shadow for Text Contrast */}
          <div 
            className="absolute inset-0 pointer-events-none z-30 rounded-3xl overflow-hidden" 
            style={{ 
              background: 'linear-gradient(to top, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.4) 30%, transparent 60%)' 
            }}
          />



          {/* Photo Indicator Dots */}
          <DiscoveryPaginationDots 
            activeIndex={cardImageIndex} 
            total={images.length} 
          />

          <motion.button
            whileTap={{ scale: 0.95 }}
            onClick={(e) => {
              e.stopPropagation();
              onSeeMore();
            }}
             className="absolute top-3 left-4 flex items-center justify-center transition-all z-50 pointer-events-auto"
          >
             <img src={typeof moreIcon === 'string' ? moreIcon : (moreIcon as any).src} alt="More" className="h-12 w-auto object-contain" style={{ filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.4))' }} />
          </motion.button>

          {/* Country Flag Badge - Top Right Sticker Style */}
          {(user.countryFlag || user.country) && (
            <div 
              className="absolute top-0 right-0 pointer-events-none flex items-center justify-center z-50 w-16 h-auto"
              style={{ 
                transform: 'translate(25%, -25%) rotate(12deg) translateZ(0)',
                filter: 'drop-shadow(4px 4px 12px rgba(0,0,0,0.6))',
                willChange: 'transform'
              }}
            >
              <FlagImage 
                countryName={user.countryFlag || user.country} 
                className="w-full h-auto"
              />
            </div>
          )}

          {/* Active recently — top left, just below the "More" button (top-3, h-12) */}
          <RecentlyActiveBadge
            userId={user.id}
            style={{ top: 68, left: 16, zIndex: 50, fontSize: 15, padding: '5px 11px' }}
          />

           <div className="absolute bottom-0 left-0 right-0 p-6 text-white pb-12 pointer-events-none z-20">
            <div className="flex flex-col items-start mb-1 h-auto">
              <h3 
                className="text-2xl mb-1 text-white flex items-center gap-2"
                style={{ textShadow: '0 2px 8px rgba(0,0,0,0.8)' }}
              >
                <div className="flex items-center gap-1">
                  {user.rank && ['watcher', 'legion', 'verified', 'union'].includes(user.rank.toLowerCase()) ? (
                    <span className="p-[1.5px] rounded-lg bg-linear-to-r from-emerald-950 via-emerald-400 to-emerald-950 shadow-[0_0_15px_rgba(52,211,153,0.3)] inline-flex items-center">
                      <span className="px-2 py-0.5 rounded-[6.5px] bg-transparent flex items-center font-bold gap-1.5">
                        <span>{user.name}</span>
                        <RankBadge rank={user.rank} size={28} />
                        <span className="opacity-60 text-lg font-light">·</span>
                        <span>{formattedAge}</span>
                      </span>
                    </span>
                  ) : (
                    <div className="flex items-center gap-1.5">
                      <span>{user.name}</span>
                      <span className="opacity-60 text-lg font-light">·</span>
                      <span>{formattedAge}</span>
                    </div>
                  )}
                </div>
              </h3>
              
               <p className="text-sm text-[#E5E5E5] mb-2 font-medium" style={{ textShadow: '0 2px 8px rgba(0,0,0,0.8)' }}>
                  @{user.username.replace(/^@/, '')}
               </p>
             </div>
             
             {((user.home_residence?.trim() || user.location?.trim() || user.country?.trim()) || user.showLocation === false) && (
               <div className="flex items-center gap-2 mb-2">
                 <MapPin className="w-3.5 h-3.5 text-[#F39C12] dark:text-[#4478ea]" style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.5))' }} />
                 <span className="text-xs text-[#E5E5E5]" style={{ textShadow: '0 2px 8px rgba(0,0,0,0.8)' }}>
                   {user.showLocation === false ? 'Earth' : (user.home_residence?.trim() || user.location?.trim() || user.country)}
                 </span>
               </div>
             )}
  
             {user.education?.trim() && (
                <div className="flex items-center gap-2 mb-2">
                  <GraduationCap className="w-3.5 h-3.5 text-[#F39C12] dark:text-[#4478ea]" style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.5))' }} />
                  <span className="text-xs text-[#E5E5E5]" style={{ textShadow: '0 2px 8px rgba(0,0,0,0.8)' }}>
                    {user.education}
                  </span>
                </div>
             )}

             {user.occupation?.trim() && (
                <div className="flex items-center gap-2 mb-3">
                  <Briefcase className="w-3.5 h-3.5 text-[#F39C12] dark:text-[#4478ea]" style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.5))' }} />
                  <span className="text-xs text-[#E5E5E5]" style={{ textShadow: '0 2px 8px rgba(0,0,0,0.8)' }}>
                    {user.occupation}
                  </span>
                </div>
             )}

             {/* Stats Row */}
             <div className="flex flex-wrap gap-3 mb-3">
               {/* 🔒 Friends Privacy: Hide if opted out, set to private, or profile is generally private */}
               {user.showFriendList !== false && 
                user.friendListVisibility !== 'private' && 
                (user as any).profilePrivacy !== 'private' && 
                !user.isPrivate && (
                 <div className="flex items-center gap-1.5">
                   <Users className="w-3.5 h-3.5 text-[#F39C12] dark:text-[#4478ea]" style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.5))' }} />
                   <span className="text-[10px] text-white font-bold" style={{ textShadow: '0 2px 4px rgba(0,0,0,0.8)' }}>
                     {formatCount(getOptimisticCount((user.friends?.length || user.friendsCount || 0), String(user.id), 'user', 'friendsCount'))}
                   </span>
                 </div>
               )}

               {/* 🔒 Yaps Privacy: Hide if opted out, or profile is generally private */}
               {user.showYaps !== false && 
                (user as any).profilePrivacy !== 'private' && 
                !user.isPrivate && (
                 <div className="flex items-center gap-1.5">
                   <MessageSquare className="w-3.5 h-3.5 text-[#F39C12] dark:text-[#4478ea]" style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.5))' }} />
                   <span className="text-[10px] text-white font-bold" style={{ textShadow: '0 2px 4px rgba(0,0,0,0.8)' }}>
                     {formatCount(getOptimisticCount((user.yapsCount || 0), String(user.id), 'user', 'yapsCount'))}
                   </span>
                 </div>
               )}

               {/* 🔒 Slaps Privacy: Hide if profile is generally private */}
               {(user as any).profilePrivacy !== 'private' && 
                !user.isPrivate && (
                 <div className="flex items-center gap-1.5">
                   <HandMetal className="w-3.5 h-3.5 text-[#F39C12] dark:text-[#4478ea]" style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.5))' }} />
                   <span className="text-[10px] text-white font-bold" style={{ textShadow: '0 2px 4px rgba(0,0,0,0.8)' }}>
                     {formatCount(getOptimisticCount((user.slapCount || 0), String(user.id), 'user', 'slapCount'))}
                   </span>
                 </div>
               )}
             </div>
  
            {user.bio?.trim() && (
              <div className="text-xs text-[#E5E5E5] leading-relaxed mb-3 line-clamp-2" style={{ textShadow: '0 2px 8px rgba(0,0,0,0.8)' }}>
                <FormattedText text={user.bio} className="inline" />
              </div>
            )}
  
            <div className="flex flex-wrap gap-2">
               {user.identities?.slice(0, 3).map((identity: string, i: number) => (
                  <IdentityTag key={i} tag={identity} variant="card" />
               ))}
             </div>
           </div>
         </div>
       </motion.div>
  );
}));

export function DiscoverScreen({ onMatch, initialCountry, onReturnToV2 }: DiscoverScreenProps) {
  const [users, setUsers] = useState<User[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showUserDetail, setShowUserDetail] = useState(false);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [showInsufficientFundsModal, setShowInsufficientFundsModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<User[]>([]);
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const [filters, setFilters] = useState<FilterOptions>(() => {
    // Restore persisted age range — age preference should survive sessions
    const savedAgeRange = SafeStorage.getItem('discover_age_range');
    const parsedAgeRange: [number, number] = savedAgeRange
      ? JSON.parse(savedAgeRange) as [number, number]
      : [18, 100];
    return {
      location: 'international',
      ageRange: parsedAgeRange,
      interests: [],
      tribes: [],
    };
  });
  const cardRef = useRef<HTMLDivElement>(null);
  const { 
    userProfile, 
    updateProfile, 
    sendFriendRequest, 
    passUser, 
    hasPendingRequestTo, 
    hasPendingRequestFrom, 
    isFriend, 
    friends = [],
    passedUserIds = new Set(), // Added default value
    likedUserIds = [], // Added likedUserIds with default value
    blockedUsers = [], // Added blockedUsers for immediate filtering
    blockedBy = []     // Users who blocked me — mirrored server-side; excluded too
  } = useUserData();
  // Blocking hides both ways: never surface someone I blocked OR who blocked me.
  const blockedEitherWay = [...new Set([...(blockedUsers || []), ...(blockedBy || [])].map(String))];
  // Search was the one surface that never applied the rule above: a blocked user
  // still appeared by name as soon as you typed it. Filtered at render so a block
  // made during the session takes effect immediately.
  const visibleSearchResults = searchResults.filter(u => !blockedEitherWay.includes(String(u.id)));
  const { unreadCount, addNotification } = useNotificationBadges();
  const { theme } = useTheme();
  const initializedRef = useRef(false);
  // Removed local seenUserIds as we now rely on global context persistence
  const [loading, setLoading] = useState(true);
  const isFetchingRef = useRef(false);
  const [isFetching, setIsFetching] = useState(false);
  const [isManualRefreshing, setIsManualRefreshing] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0); 
  const [viewingUser, setViewingUser] = useState<User | null>(null);
  const [showMatchModal, setShowMatchModal] = useState(false);
  const [matchedUser, setMatchedUser] = useState<User | null>(null);
  const activeCardRef = useRef<SwipeableCardHandle>(null);
  const swipedUserIds = useRef<Set<string>>(new Set());
  const isMounted = useRef(true);
  const initialLoadDone = useRef(false);
  const hasExhaustedDBRef = useRef(false);
  const isSwiping = useRef(false);
  const [isSettling, setIsSettling] = useState(false);
  const [isShaking, setIsShaking] = useState(false);
  const [swipeMode, setSwipeMode] = useState<SwipeMode>('classic');

  // ✅ Restore swipe position after users load
  useEffect(() => {
    if (users.length === 0) return;
    const savedUserId = SafeStorage.getItem('discover_last_user_id');
    if (!savedUserId) return;
    const restoredIdx = users.findIndex(u => String(u.id) === savedUserId);
      if (restoredIdx > 0 && restoredIdx < users.length) {
        setCurrentIndex(restoredIdx);
        console.log(`[Discover] Restored position to index ${restoredIdx} (user ${savedUserId})`);
        
        // 🔒 SAFETY RELOAD: If we restored near the end of the batch, 
        // trigger a background reload immediately to prevent empty screen.
        if (restoredIdx >= users.length - 5) {
           console.log(`[Discover] Restored near end of batch. Triggering aggressive reload...`);
           loadMoreUsers();
        }
      }
    // DO NOT clear last_user_id here. Let it persist for background fetches.
  }, [users.length > 0]); // Only triggers when users first populate

  // Navigation helper
  const navigateToMarket = () => {
    setShowInsufficientFundsModal(false);
    window.dispatchEvent(new CustomEvent('navigateToScreen', { detail: SCREENS.STORE }));
  };

  /**
   * 🔄 LAZY LOAD ACCURATE STATS
   * Since the initial Discover batch only contains 'stale' document fields, 
   * we on-demand fetch real Friends/Yaps/Slaps counts for the top of the stack.
   */
  useEffect(() => {
    const fetchStatsForVisibleCards = async () => {
      if (users.length === 0 || currentIndex >= users.length) return;

      // 1. Determine which users to fetch (active + next 1 for prefetch)
      const usersToFetch = users.slice(currentIndex, currentIndex + 2);
      
      for (const targetUser of usersToFetch) {
        // Skip if already fetched/populated with non-zero (unless we want to force refresh)
        // Actually, since the user says "Still showing zero", we should trust our fetcher.
        // We'll use a local 'fresh' flag or just check if friendsCount === undefined/null/0 
        // to avoid infinite loops, but here we'll just check if it's already "processed".
        if ((targetUser as any)._statsLoaded) continue;

        try {
          const stats = await UserService.fetchUserStats(String(targetUser.id));
          
          setUsers(prev => prev.map(u => 
            String(u.id) === String(targetUser.id) 
              ? { ...u, ...stats, _statsLoaded: true } as User 
              : u
          ));
        } catch (err) {
          console.warn(`[Discover] Failed to lazy-load stats for ${targetUser.id}:`, err);
        }
      }
    };

    fetchStatsForVisibleCards();
  }, [currentIndex, users.length]); // Re-run when stack moves or loads

  // 🥊 REAL-TIME SLAP: Listen for incoming slaps while browsing
  useEffect(() => {
    const handleRealTimeSlap = (e: any) => {
      const { fromUserId, slapsBurst } = e.detail;
      const currentUserInStack = users[currentIndex];
      
      // If the person currently on the card slapped us, trigger a SHAKE!
      if (currentUserInStack && String(fromUserId) === String(currentUserInStack.id)) {
        console.log(`[DiscoverScreen] LIVE BATTLE: Received ${slapsBurst} slaps from ${currentUserInStack.name}! Shaking...`);
        setIsShaking(true);
        setTimeout(() => setIsShaking(false), 500);
      }
    };

    window.addEventListener(EVENTS.REAL_TIME_SLAP, handleRealTimeSlap);
    return () => window.removeEventListener(EVENTS.REAL_TIME_SLAP, handleRealTimeSlap);
  }, [currentIndex, users]);

  const [isRefreshMode, setIsRefreshMode] = useState(true); // 🔒 Enabled by default: Users should always see a full stack

  // Fetch users from backend
  // silent=true: activates refreshMode but suppresses the loading spinner (used for auto-fallback)
  const loadMoreUsers = async (overrideRefreshMode?: boolean, silent = false) => {
    const effectiveRefreshMode = overrideRefreshMode !== undefined ? overrideRefreshMode : isRefreshMode;
    if (!userProfile?.id || isFetchingRef.current) return;

    // 🔒 ULTIMATE GUARD: If we have reached the end of the stack, strictly block 
    // any automatic background fetches. Only a manual click (overrideRefreshMode=true) 
    // is allowed to proceed. This prevents the "auto-refreshing" ghost.
    const isReachedEnd = users.length > 0 && currentIndex >= users.length;
    if (isReachedEnd && !effectiveRefreshMode) {
        console.log('[Discover] Blocking automatic background fetch because end-of-stack is reached.');
        return;
    }

    // 🔒 REFRESH LOGIC: When explicitly refreshing, clear the deck immediately
    if (overrideRefreshMode === true) {
      setUsers([]);
      setCurrentIndex(0);
      setIsSettling(true);
    }

    isFetchingRef.current = true;
    setIsFetching(true);
    
    // 🔒 REFINED LOADING: ONLY show loader on initial enter OR an explicit user-triggered refresh.
    const isExplicitRefresh = overrideRefreshMode === true && !silent;
    if (!initialLoadDone.current || isExplicitRefresh) {
      setLoading(true);
    }
    if (isExplicitRefresh) {
      hasExhaustedDBRef.current = false;
    }
    const startTime = Date.now();
    let isFallback = false; // 🔒 Track if we are chaining a fallback call
    try {
      // Get current friends to exclude (Always exclude friends confirmed)
      // Ids only: this list is used for exclusion and nothing else, and
      // getFriends would fetch every friend's full profile to build it.
      const friendIds = await UserService.getFriendIds(String(userProfile.id));

      // Combine friends, PERSISTED interactions (passed/liked), AND currently loaded users to exclude
      // In refresh mode, skip currentLoadedIds: setUsers([]) is async so `users` closure
      // still has the old list — adding them would exclude every previously seen user.
      const currentLoadedIds = effectiveRefreshMode ? [] : users.map(u => String(u.id));
      
      // Base exclusions: Friends + Currently Loaded + Session Swipes + Blocked Users
      let excludeIds = [
        ...friendIds, 
        ...currentLoadedIds,
        ...Array.from(swipedUserIds.current),
        ...blockedEitherWay
      ];

      // 🔒 REFRESH LOGIC: Even in refresh mode, we MUST exclude friends.
      // We don't exclude historical passes/likes if we want them to reappear, 
      // but we will prioritize new users during sorting.
      if (!effectiveRefreshMode) {
        excludeIds = [
            ...excludeIds,
            ...Array.from(passedUserIds),
            ...likedUserIds
        ];
      }

      // De-duplicate
      excludeIds = [...new Set(excludeIds)];

      // 🛡️ DYNAMIC AGE SAFETY AT FETCH TIME
      // Only override if the range is a minor range while user is adult (or vice versa).
      // Do NOT override a valid adult-range set by the user.
      let effectiveAgeRange = filters.ageRange;
      // Fail-closed: unknown age (null) leaves the filter unchanged — DiscoverFilterModal
      // already defaulted it to the minor range via its own fail-closed gate.
      const currentAge = calculateAge(userProfile.dateOfBirth);
      if (currentAge !== null && currentAge < 18) {
        if (effectiveAgeRange[0] >= 18) {
           effectiveAgeRange = [13, 17];
           setFilters(prev => ({ ...prev, ageRange: [13, 17] }));
        }
      } else if (currentAge !== null && currentAge >= 18) {
        if (effectiveAgeRange[1] < 18) {
           effectiveAgeRange = [18, 100];
           setFilters(prev => ({ ...prev, ageRange: [18, 100] }));
        }
      }

      // Derive bracket for list-rule compliance. Fail-closed: null age → 'minor'.
      const viewerAgeGroup: 'adult' | 'minor' = (currentAge !== null && currentAge >= 18) ? 'adult' : 'minor';
      const newUsers = await DiscoverService.fetchClassicPool(
        String(userProfile.id),
        excludeIds,
        { filters, targetSize: 20, refreshKey, viewerAgeGroup }
      );
      console.log(`[Discover] Fetched ${newUsers.length} users. Refresh: ${isExplicitRefresh}. Excluded: ${excludeIds.length}`);
      
      // 🔒 PRIORITY WEIGHTING
      // 1. Pending Requests (Matches) -> FRONT
      // 2. New Users (Never seen) -> MIDDLE
      // 3. Interacted Users (Passed/Liked) -> BACK
      const weightedUsers = [...newUsers].sort((a, b) => {
        const aId = String(a.id);
        const bId = String(b.id);
        
        const aHasRequest = hasPendingRequestFrom(aId) ? 1 : 0;
        const bHasRequest = hasPendingRequestFrom(bId) ? 1 : 0;
        
        if (aHasRequest !== bHasRequest) return bHasRequest - aHasRequest;

        // Both or neither have requests, check interaction history
        const aIsNew = !passedUserIds.has(aId) && !likedUserIds.includes(aId);
        const bIsNew = !passedUserIds.has(bId) && !likedUserIds.includes(bId);

        if (aIsNew !== bIsNew) return aIsNew ? -1 : 1;

        // If both new or both seen, randomize
        return Math.random() - 0.5;
      });

      if (weightedUsers.length > 0) {
        setUsers(prev => {
          // Avoid duplicates and enforce strict age safety on client-side
          const existingIds = new Set(prev.map(u => String(u.id)));
          const seenInBatch = new Set<string>();

          return [...prev, ...weightedUsers.filter(u => {
            const uid = String(u.id);
            
            // 0. Strict Relationship Guard: Never show friends or self
            if (isFriend(uid) || uid === String(userProfile.id)) return false;
            
            // 1. Check against existing stack
            if (existingIds.has(uid)) return false;
            
            // 2. Check against duplicate in current batch
            if (seenInBatch.has(uid)) return false;
            
            // 3. Check against Session Swipes (Immediate visual consistency)
            if (swipedUserIds.current.has(uid)) return false;
            
            // 4. Filter ghost: never show incomplete/unfinished accounts
            if (!u.name || u.name.trim() === '' || u.name === 'User') return false;
            if (!u.setupComplete && !u.portfolio?.some((p: string) => p)) return false;

            // 5. Age Strictness — fail-closed: unknown DOB → exclude
            const age = calculateAge(u.dateOfBirth);
            if (age === null || age < Number(effectiveAgeRange[0]) || age > Number(effectiveAgeRange[1])) return false;

            seenInBatch.add(uid);
            return true;
          })];
        });
      } else {
        console.log('[Discover] No new users found. DB Exhausted for current batch parameters.');
        hasExhaustedDBRef.current = true;
        // 🔒 AUTO-FALLBACK REMOVED: We no longer automatically search for seen users. 
        // We now respect the user's manual control and stay on the empty screen until they click Refresh.
        console.log('[Discover] No more new users. Waiting for manual user action.');
      }
    } catch (error) {
      console.error("Error loading users:", error);
    } finally {
      // 🔒 If we triggered a fallback, CLEAN UP current lock first
      if (isFallback) {
        isFetchingRef.current = false;
        setIsFetching(false);
        setIsManualRefreshing(false);
        return;
      }

      const elapsed = Date.now() - startTime;
      const minDelay = !initialLoadDone.current ? 5000 : 0; // 🔒 PREMIUM: Wait 5s on first load for a 'deep search' feel
      const remaining = Math.max(0, minDelay - elapsed);
      
      const finalizeLoading = () => {
        setLoading(false);
        isFetchingRef.current = false;
        setIsFetching(false);
        setIsManualRefreshing(false);
        initialLoadDone.current = true;

        // 🔒 PROACTIVE REFILL REDUCED: We no longer auto-chain fetches in the background.
        // This ensures the user hits the "No more users" screen and stays there.
      };

      if (remaining > 0 && !initialLoadDone.current) {
        setTimeout(() => {
          finalizeLoading();
          // After loading ends, wait a bit for state to settle before showing 'no users'
          setIsSettling(true);
          setTimeout(() => {
            if (isMounted.current) setIsSettling(false);
          }, 500); 
        }, remaining);
      } else {
        finalizeLoading();
        setIsSettling(true);
        setTimeout(() => {
          if (isMounted.current) setIsSettling(false);
        }, 500);
      }
    }
  };

  useScreenReady(!loading);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Initial load
  // NOTE: initialCountry is intentionally excluded from deps here — the
  // initialCountry useEffect (below) already handles resets via refreshKey,
  // so including it here would cause loadMoreUsers() to fire twice.
  useEffect(() => {
    if (userProfile?.id) {
      loadMoreUsers();
    }
  }, [userProfile?.id, refreshKey]);

  // Handle initialCountry changes (e.g. from Explore screen)
  useEffect(() => {
    if (initialCountry) {
      setUsers([]);
      setCurrentIndex(0);
      hasExhaustedDBRef.current = false;
      setRefreshKey(prev => prev + 1);
    }
  }, [initialCountry]);

  // 🔒 Proactive background refill DISABLED
  // We now stay on the empty screen after 15 swipes until the user clicks Refresh.
  /*
  useEffect(() => {
    if (users.length > 0 && currentIndex < users.length) {
      if (users.length - currentIndex <= 5 && !isFetchingRef.current && !isManualRefreshing && !hasExhaustedDBRef.current) {
        console.log(`[Discover] Low on cards (${users.length - currentIndex} left). Triggering proactive background fetch...`);
        loadMoreUsers(false, true);
      }
    }
  }, [currentIndex, users.length]);
  */

  // 🔒 PATCH: Retrospective Deduplication & Cleanup
  // This ensures that if duplicates slipped in (or exist from before), they are removed before being shown.
  useEffect(() => {
    if (users.length === 0) return;

    const uniqueUsers: typeof users = [];
    const seenIds = new Set<string>();
    let hasChanges = false;
    let newCurrentIndex = currentIndex;

    users.forEach((user, index) => {
      const uid = String(user.id);
      
      // 🔒 PATCH: Immediately remove blocked users from stack, even if they are history or current
      if (blockedEitherWay.includes(uid)) {
        hasChanges = true;
        if (index < currentIndex) {
          newCurrentIndex--;
        }
        return;
      }

      // 🔒 SAFETY: Always preserve history and current card (to prevent index shifts during swipe)
      if (index <= currentIndex) {
        seenIds.add(uid);
        uniqueUsers.push(user);
        return;
      }

      // Check absolute exclusions for FUTURE cards only
      // 🔒 REFRESH: If in refresh mode, relax passed/liked checks.
      const isExcluded = 
        seenIds.has(uid) || 
        uid === String(userProfile?.id) ||
        isFriend(uid) ||
        (!isRefreshMode && (passedUserIds.has(uid) || likedUserIds.includes(uid)));

      if (isExcluded) {
        hasChanges = true;
        return;
      }

      seenIds.add(uid);
      uniqueUsers.push(user);
    });

    if (hasChanges) {
      console.log(`[Discover] Cleanup: Removed ${users.length - uniqueUsers.length} duplicates/excluded users from stack.`);
      setUsers(uniqueUsers);
      if (newCurrentIndex !== currentIndex) {
        setCurrentIndex(Math.max(0, newCurrentIndex));
      }
    }
  }, [users.length, passedUserIds.size, likedUserIds.length, blockedEitherWay.length, friends.length, isRefreshMode]); // 🔒 PERF: Removed currentIndex — dedup does not need to re-run on every swipe

  // 🔒 PATCH: Auto-retry and Safety Unlock
  useEffect(() => {
    if (loading && userProfile?.id) {
      const safetyTimer = setTimeout(() => {
        if (loading) {
          console.log("[Discover] Safety unlocking loading state...");
          setLoading(false);
          isFetchingRef.current = false;
          setIsFetching(false);
        }
      }, 5000); // 5s safety timeout
      return () => clearTimeout(safetyTimer);
    }

    // 🔒 Guard with initialLoadDone: do NOT retry until the very first fetch has
    // fully settled. Without this, a slow first-load + the 5s safety unlock would
    // immediately kick off another refreshKey increment, restarting the loading screen.
    // 🔒 AUTO-RECOVERY: If the screen is blank for > 2s while NOT loading,
    // automatically trigger a refresh to ensure we didn't get stuck in a locked state.
    if (users.length === 0 && !loading && !searchQuery && userProfile?.id && initialLoadDone.current) {
      const timer = setTimeout(() => {
        if (users.length === 0 && !isFetchingRef.current) {
            console.log("[Discover] Auto-recovery: Refreshing empty stack...");
            setRefreshKey(prev => prev + 1);
        }
      }, 2500); 
      return () => clearTimeout(timer);
    }
  }, [users.length, loading, searchQuery, userProfile?.id]);

  // Re-fetch when filters change
  useEffect(() => {
    if (initializedRef.current) {
      console.log("[Discover] Filters changed, resetting stack:", filters);
      setUsers([]);
      setCurrentIndex(0);
      swipedUserIds.current.clear(); // Reset session history on filter change
      setRefreshKey(prev => prev + 1);
    }
    initializedRef.current = true;
  }, [filters]);

  // 🛡️ AGE SAFETY: Correct filters on mount if user is a minor.
  useEffect(() => {
    if (userProfile?.id) {
      const currentAge = calculateAge(userProfile.dateOfBirth);
      if (currentAge !== null && currentAge < 18) {
        if (filters.ageRange[0] >= 18) {
          setFilters(prev => ({ ...prev, ageRange: [13, 17] }));
        }
      } else if (currentAge !== null && currentAge >= 18) {
        if (filters.ageRange[1] < 18) {
           setFilters(prev => ({ ...prev, ageRange: [18, 100] }));
        }
      }
    }
  }, [userProfile?.id, userProfile?.dateOfBirth]);
  const currentUser = users[currentIndex];

  // 🔒 PATCH: Continuously log the active user so switching tabs keeps your spot
  useEffect(() => {
    if (currentUser) {
      SafeStorage.setItem('discover_last_user_id', String(currentUser.id));
    }
  }, [currentUser?.id]);

  // 🔒 OPT-OUT GATE: Users who disabled "Show in Discover" cannot swipe others
  if (userProfile && userProfile.showInDiscover === false) {
    return (
      <div className="w-full h-screen bg-light-pattern dark:bg-[#0D0D0D] flex flex-col items-center justify-center p-8 relative">
        <div className="absolute inset-0 pointer-events-none">
          <ParallaxBackground speed={0.05} />
        </div>
        <div className="relative z-10 text-center max-w-sm">
          <div className="w-20 h-20 rounded-full bg-black/10 dark:bg-white/10 flex items-center justify-center mx-auto mb-6">
            <X className="w-10 h-10 text-[#4478ea]" />
          </div>
          <h2 className="text-2xl font-bold text-black dark:text-white mb-3">You're Hidden from Discover</h2>
          <p className="text-[#666666] dark:text-[#a0a0a0] text-sm leading-relaxed mb-6">
            You've opted out of appearing on the Discover screen. To swipe and connect with others, re-enable <strong>"Show me in Discover"</strong> in your Edit Profile settings.
          </p>
          <motion.button
            whileTap={{ scale: 0.95 }}
            onClick={() => window.dispatchEvent(new CustomEvent('navigateToScreen', { detail: 'editProfile' }))}
            className="px-6 py-3 rounded-full text-white font-semibold text-sm"
            style={{ background: 'linear-gradient(90deg, #4478ea, #2d1ef1)' }}
          >
            Go to Edit Profile
          </motion.button>
        </div>
      </div>
    );
  }


  // Removed old drag handlers in favor of isolated component

  // activeCardRef is defined above

  const advanceStack = () => {
    setCurrentIndex((prev) => {
        const nextIndex = (prev + 1);
        
        // Standard flow
        const nextUser = users[nextIndex];
        
        if (!nextUser) {
          SafeStorage.removeItem('discover_last_user_id');
        }
        
        // 🔒 AUTO-REFILL DISABLED: We now strictly wait for the user to reach the end 
        // and manually click Refresh. This keeps the UX calm and stable.
        
        // 🔒 SAFETY: Trigger a brief 'settling' buffer on every swipe to prevent
        // the "No more users" alert from flickering during the index transition.
        setIsSettling(true);
        setTimeout(() => {
          if (isMounted.current) setIsSettling(false);
        }, 400);

        return nextIndex;
    });
  };

  const performLikeAction = async (fromDrag = false, mode: SwipeMode = 'classic') => {
    if (isSwiping.current || !currentUser) return;

    // 🔒 CONCURRENCY LOCK
    isSwiping.current = true;

    const uid = String(currentUser.id);
    
    // 🔒 SAFE RE-SWIPE CHECK:
    // If we have ALREADY requested or are friends, DO NOT CHARGE, just skip.
    const isAlreadyInteracted = 
        hasPendingRequestTo(uid) || 
        isFriend(uid) || 
        likedUserIds.includes(uid);
    
    if (isAlreadyInteracted) {
         console.log(`[Discover] Already interacted with ${currentUser.username}. Skipping charge/request.`);
         // Just perform visual swipe
         if (!fromDrag && activeCardRef.current) {
             await activeCardRef.current.triggerSwipe('right');
         }
         
         // Move to next
         swipedUserIds.current.add(uid);
         advanceStack();
         isSwiping.current = false;
         return;
    }

    // 🤝 MUTUAL MATCH FAST-PATH:
    // If they already sent us a request, this swipe right = accepting their request.
    // No gold charge — we're completing their connection, not initiating one.
    const isPendingFromThem = hasPendingRequestFrom(uid);
    if (isPendingFromThem) {
      console.log(`[Discover] ${currentUser.username} already sent a request — auto-accepting as mutual match (no gold charge).`);
      
      // Run the visual swipe animation
      if (!fromDrag && activeCardRef.current) {
        await activeCardRef.current.triggerSwipe('right');
      }

      swipedUserIds.current.add(uid);
      const swipedUser = { ...currentUser };
      advanceStack();

      // Process the mutual match in the background (no gold deduction)
      (async () => {
        try {
          const result = await sendFriendRequest(
            String(swipedUser.id),
            swipedUser.name,
            swipedUser.username,
            getUserAvatar(swipedUser)
          );

          if (result?.isMatch && result?.userData) {
            addNotification({
              type: 'friend_accepted',
              title: 'You are now new Friends!',
              message: `You and ${swipedUser.name} are now connected. Start a conversation, slap them or stalk their yaps.`,
              userId: String(userProfile.id),
              userAvatar: getUserAvatar(swipedUser),
              userName: swipedUser.name,
              metadata: { friendId: String(swipedUser.id) }
            });
            setMatchedUser({
              ...swipedUser,
              ...result.userData,
              age: calculateAge(swipedUser.dateOfBirth) ?? undefined,
              image: getUserAvatar(swipedUser),
            });
            onMatch?.({
              ...swipedUser,
              ...result.userData,
              age: calculateAge(swipedUser.dateOfBirth) ?? undefined
            });
            setShowMatchModal(true);
          }
        } catch (error) {
          console.error("[Discover] Mutual match processing error:", error);
        }
      })();

      isSwiping.current = false;
      return;
    }

    // 💰 Local Gold Check (only reached if no pending request from them)
    const cost = SWIPE_COSTS[mode];
    if ((userProfile.gold || userProfile.goldBalance || 0) < cost) {
      console.warn('[Discover] Insufficient gold (local check)');
      setSwipeMode(mode);
      setShowInsufficientFundsModal(true);
      isSwiping.current = false;
      return;
    }

    // 🔒 Trigger UI Animation if not from drag
    if (!fromDrag && activeCardRef.current) {
      await activeCardRef.current.triggerSwipe('right');
    }

    swipedUserIds.current.add(uid);
    const swipedUser = { ...currentUser };

    // 🛰️ BACKGROUND PROCESSING
    (async () => {
      try {
        const actionType = mode === 'classic' ? 'discover_swipe_right' : 'discover_swipe_matching';
        const econResult = await EconomyService.deductGold(String(userProfile.id), cost, actionType);
        if (econResult.success && econResult.newBalance !== undefined) {
          updateProfile({ gold: econResult.newBalance, goldBalance: econResult.newBalance });
        }

        const result = await Promise.race([
          sendFriendRequest(
            String(swipedUser.id),
            swipedUser.name,
            swipedUser.username,
            // Use the best available avatar: cropped preview > croppedImage > raw image
            getUserAvatar(swipedUser),
            calculateAge(swipedUser.dateOfBirth) ?? undefined
          ),
          new Promise<any>((_, reject) => setTimeout(() => reject(new Error("Friend request timed out")), 15000))
        ]);

        if (result?.isMatch && result?.userData) {
          addNotification({
            type: 'friend_accepted',
            title: 'You are now friends!',
            message: `You and ${swipedUser.name} are now connected. Start a conversation, slap them or stalk their yaps.`,
            userId: String(userProfile.id),
            userAvatar: getUserAvatar(swipedUser),
            userName: swipedUser.name,
            metadata: { friendId: String(swipedUser.id) }
          });
            setMatchedUser({
              ...swipedUser,
              ...result.userData,
              age: calculateAge(swipedUser.dateOfBirth) ?? undefined,
              image: getUserAvatar(swipedUser),
            });
          onMatch?.({
            ...swipedUser,
            ...result.userData,
            age: calculateAge(swipedUser.dateOfBirth) ?? undefined
          });
        }
      } catch (error) {
        console.error("[Discover] Like processing error:", error);
      }
    })();

    // Advance Stack
    if (!fromDrag) {
       // Wait slightly if button pressed to let animation show?
       // triggerSwipe awaits animation so we are good.
    }

    advanceStack();
    isSwiping.current = false;
  };

  const handleLike = () => performLikeAction(false, 'classic');

  const performPassAction = async (fromDrag = false) => {
     if (isSwiping.current || !currentUser) return;
     isSwiping.current = true;
     const uid = String(currentUser.id);
     swipedUserIds.current.add(uid);

     // Only send backend pass request if we haven't already passed them
     if (!passedUserIds.has(uid) && !likedUserIds.includes(uid) && !isFriend(uid)) {
         passUser(uid).catch(console.error);
     }

     if (!fromDrag && activeCardRef.current) {
       await activeCardRef.current.triggerSwipe('left');
     }

     advanceStack();
     isSwiping.current = false;
  };

  const handlePass = () => performPassAction(false);

  const handleCardSwipe = (direction: 'left' | 'right') => {
    if (direction === 'right') performLikeAction(true, 'classic');
    else performPassAction(true);
  };

  const handleSeeMore = (user: any) => {
    setShowUserDetail(true);
  };

  const searchTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (query.trim().length > 1) {
      searchTimeoutRef.current = setTimeout(async () => {
        try {
          // Normalize to lowercase so "ABC" finds "@abc" — case-insensitive search
          const sAge = calculateAge(userProfile.dateOfBirth);
          const sAgeGroup: 'adult' | 'minor' = (sAge !== null && sAge >= 18) ? 'adult' : 'minor';
          const viewerIsMod = ['legion', 'watcher'].includes(String((userProfile as any)?.rank || '').toLowerCase());
          if (viewerIsMod) {
            // Mods search ALL users across age brackets (server-verified) so they
            // can find minors to enforce the rules.
            const { getFunctions, httpsCallable } = await import('firebase/functions');
            const fn = httpsCallable(getFunctions(), 'modSearchUsers');
            const res: any = await fn({ searchTerm: query });
            setSearchResults((res?.data?.users || []) as any);
            setShowSearchDropdown(true);
            return;
          }
          const results = await SearchService.searchUsers(query.toLowerCase(), String(userProfile.id), sAgeGroup, viewerIsMod);
          setSearchResults(results);
          setShowSearchDropdown(true);
        } catch (error) {
          console.error("Search failed:", error);
        }
      }, 500); // 500ms debounce
    } else {
      setSearchResults([]);
      setShowSearchDropdown(false);
    }
  };


  const handleUserSelect = (user: any) => {
    // Navigate to friend profile
    window.dispatchEvent(new CustomEvent('navigateToFriendProfile', { detail: user }));
    setSearchQuery('');
    setShowSearchDropdown(false);
  };



  return (
    <motion.div
      animate={isShaking ? {
        x: [0, -10, 10, -10, 10, -5, 5, 0],
        y: [0, 5, -5, 5, -5, 0],
        rotate: [0, -1, 1, -1, 1, 0]
      } : { x: 0, y: 0, rotate: 0 }}
      transition={{ duration: 0.4 }}
      className="w-full h-screen bg-light-pattern dark:bg-[#0D0D0D] relative overflow-hidden"
    >
      <InsufficientFundsModal
        isOpen={showInsufficientFundsModal}
        onClose={() => setShowInsufficientFundsModal(false)}
        onGoToMarket={navigateToMarket}
        requiredAmount={SWIPE_COSTS[swipeMode]}
        currentAmount={userProfile.gold || 0}
        customMessage={swipeMode === 'matching'
          ? `You need 30 gold to connect with someone like this. You have ${userProfile.gold || 0} gold.`
          : `You need 10 gold to swipe. You have ${userProfile.gold || 0} gold.`
        }
      />
      <div className="absolute inset-0 pointer-events-none">
        <ParallaxBackground speed={0.05} />
      </div>
      <div
        className="w-full max-w-2xl mx-auto h-full flex flex-col relative z-10 px-6"
        style={{
          paddingTop: 'max(60px, calc(env(safe-area-inset-top) + 24px))',
          paddingBottom: 'calc(6rem + env(safe-area-inset-bottom))',
        }}
      >
        <div className="w-full max-w-md mx-auto h-full flex flex-col overflow-visible">
          {/* Header */}
          <div className="mb-4 flex items-center justify-center relative min-h-[40px]">
            {/* Logo and Notifications on the left */}
            <div className="absolute left-0 flex items-center gap-2">
              <img src={theme === 'dark' ? (logoImageWhite as any) : (logoImage as any)} alt="AfroYap" className="h-6 w-auto" />
              <div className="flex items-center gap-1">
                <motion.button
                  whileTap={{ scale: 0.95 }}
                  onClick={() => window.dispatchEvent(new CustomEvent('navigateToScreen', { detail: 'notifications' }))}
                  className="relative p-1 rounded-full hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
                >
                  <Bell className="w-5 h-5 text-black dark:text-white" />
                  {unreadCount > 0 && (
                    <span className={`absolute -top-1 -left-1 ${unreadCount > 9 ? 'min-w-[22px] h-[18px] px-1' : 'w-4 h-4'} bg-[#E74C3C] dark:bg-[#3498DB] text-white text-[10px] flex items-center justify-center rounded-full border border-[#fff] dark:border-[#0D0D0D] font-bold`}>
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </motion.button>
                
              </div>
            </div>

            <h2 className="text-3xl text-black dark:text-white" style={{ fontFamily: "'NarnialOne', 'Narniaone', 'NARNIAONE', 'Poppins', sans-serif" }}>Discover</h2>

            <div className="absolute right-0">
              {/* Market / Gold Display */}
              <motion.button
                whileTap={{ scale: 0.95 }}
                // onClick={() => window.dispatchEvent(new CustomEvent('navigateToScreen', { detail: 'store' }))}
                className="flex items-center gap-1.5 bg-black/5 dark:bg-white/5 px-3 py-1.5 rounded-full border border-black/10 dark:border-white/10 backdrop-blur-sm opacity-50 cursor-not-allowed"
              >
                <img src={goldIcon as any} alt="Gold" className="h-4 w-auto object-contain" />
                <span className="text-black dark:text-white text-xs font-bold">{Number(userProfile.goldBalance || 0).toLocaleString()}</span>
              </motion.button>
            </div>
          </div>

          {/* Search Bar and Filter */}
          <div className="mb-3 flex gap-2" ref={cardRef}>
            <div className="flex-1 relative">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#666666] dark:text-[#E5E5E5]" />
                <Input
                  type="text"
                  value={searchQuery}
                  placeholder="Search @username..."
                  onChange={handleSearchChange}
                  className="w-full pl-12 h-12 bg-[#F5F5F5] dark:bg-[#2a2a2a] border-none text-[#ea580c] dark:text-white placeholder:text-[#999999] dark:placeholder:text-[#E5E5E5] rounded-xl shadow-[2px_2px_0px_0px_rgba(0,0,0,0.25)] dark:shadow-[2px_2px_0px_0px_rgba(255,255,255,0.1)]"
                />
              </div>
              {showSearchDropdown && (
                <div className="absolute left-0 top-full w-full bg-[#eddeb7] dark:bg-[#1a1a1a] border border-black/10 dark:border-white/10 rounded-b-xl shadow-lg z-50 mt-1">
                  {visibleSearchResults.length > 0 ? (
                    visibleSearchResults.map(user => (
                      <div
                        key={user.id}
                        className="px-4 py-3 cursor-pointer hover:bg-[#F5F5F5] dark:hover:bg-[#eddeb7]/5 transition-colors flex items-center gap-3 border-b border-black/5 dark:border-white/5 last:border-b-0"
                        onClick={() => {
                          handleUserSelect(user);
                          setSearchQuery('');
                          setShowSearchDropdown(false);
                        }}
                      >
                        <div style={{ position: 'relative', flexShrink: 0 }}>
                          <img src={user.image} alt={user.name} className="w-10 h-10 rounded-full object-cover" />
                          <ActivityStatus userId={user.id} variant="dot" ringColor={theme === 'dark' ? '#1a1a1a' : '#eddeb7'} style={{ width: 11, height: 11 }} />
                        </div>
                        <div>
                          <p className="text-sm text-[#ea580c] dark:text-white">{user.name}</p>
                          <p className="text-xs text-[#666666] dark:text-[#E5E5E5]">@{user.username.replace(/^@/, '')}</p>
                          <ActivityStatus userId={user.id} style={{ fontSize: 11, marginTop: 1 }} />
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="px-4 py-6 text-center">
                      <p className="text-sm text-[#666666] dark:text-[#E5E5E5]">No results found</p>
                    </div>
                  )}
                </div>
              )}
            </div>

            <motion.button
              data-tour="guide-swipe-filter"
              whileTap={{ scale: 0.95 }}
              onClick={() => setShowFilterModal(true)}
              className="w-12 h-12 rounded-xl bg-brand-gradient flex items-center justify-center text-white shadow-lg shrink-0 button-shadow"
            >
              <SlidersHorizontal className="w-5 h-5 text-white" />
            </motion.button>
          </div>

          {/* Active Filters Badge */}
          {(filters.interests?.length > 0 || filters.tribes?.length > 0 || filters.location === 'same-country') && (
            <div className="mb-3 flex flex-wrap gap-2">
              {filters.location === 'same-country' && (
                <Badge className="bg-[#F39C12] dark:bg-[#4478ea] text-white">
                  {userProfile.country || 'Same Country'}
                </Badge>
              )}
              {filters.interests?.map((interest) => (
                <Badge key={interest} className="bg-[#E74C3C] dark:bg-[#2d1ef1] text-white">
                  {interest}
                </Badge>
              ))}
              {filters.tribes?.map((tribe) => (
                <Badge key={tribe} className="bg-[#F39C12] dark:bg-[#4478ea] text-white">
                  {tribe}
                </Badge>
              ))}
            </div>
          )}

          {/* Main Discover Viewport */}
          <div className="relative w-full flex-1 flex flex-col min-h-0">
            {loading ? (
              // Same reasoning as V2: the route overlay does not cover a cold
              // load or a load past its 5s ceiling, and null left the viewport
              // empty. V1 fetches as one unit, so there is no honest per-section
              // number to report — it gets the pulse without a progress bar.
              <div className="fixed inset-0 z-[100]"><ScreenLoader /></div>
            ) : !currentUser ? (
              <motion.div 
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="flex-1 flex flex-col items-center justify-center text-center w-full px-6 py-4 min-h-[300px]"
              >
                <div className="relative mb-6">
                  <motion.div 
                    animate={{ rotate: 360 }}
                    transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                    className="absolute inset-0 rounded-full blur-3xl opacity-20 bg-brand-gradient"
                  />
                  <div className="relative w-20 h-20 rounded-full bg-linear-to-b from-[#4478ea]/20 to-transparent flex items-center justify-center backdrop-blur-md border border-white/10">
                    {isFetching ? (
                       <motion.div
                         animate={{ rotate: 360 }}
                         transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }}
                       >
                         <Users className="w-10 h-10 text-white opacity-40" />
                       </motion.div>
                    ) : (
                       <Users className="w-10 h-10 text-white" />
                    )}
                    <motion.div 
                      animate={{ scale: [1, 1.2, 1], opacity: [0.5, 1, 0.5] }}
                      transition={{ duration: 2, repeat: Infinity }}
                      className="absolute -top-1 -right-1"
                    >
                      <Sparkles className="w-6 h-6 text-white/80" />
                    </motion.div>
                  </div>
                </div>

                <h2 className="text-2xl font-normal text-black dark:text-white mb-3 tracking-tight" style={{ fontFamily: "NarnialOne, Narniaone, NARNIAONE, Poppins, sans-serif" }}>
                  Refresh Users or Adjust Filters
                </h2>
                <p className="text-[#666666] dark:text-[#a0a0a0] text-sm leading-relaxed mb-8 max-w-[280px]">
                  You've reached the end of the current stack. Refresh to see who else is on the app or add filters!
                </p>

                <div className="flex flex-col gap-3 w-full max-w-[240px]">
                  <Button
                    onClick={() => setShowFilterModal(true)}
                    className="w-full h-12 rounded-2xl text-base font-bold shadow-lg transition-all hover:scale-[1.02] active:scale-[0.98]"
                    style={{ background: 'linear-gradient(135deg, #2D5CFE 0%, #1A3FB5 100%)', color: 'white' }}
                  >
                    Adjust Filters
                  </Button>
                  
                  <Button
                    variant="ghost"
                    onClick={() => {
                       setUsers([]);
                       setCurrentIndex(0);
                       swipedUserIds.current.clear();
                       SafeStorage.removeItem('discover_last_user_id');
                       setIsRefreshMode(true);
                       setIsManualRefreshing(true);
                       loadMoreUsers(true);
                    }}
                    disabled={isManualRefreshing}
                    className="w-full h-12 rounded-2xl text-base font-semibold text-black dark:text-white hover:bg-black/5 dark:hover:bg-white/5 transition-all outline-none border-none"
                  >
                    {isManualRefreshing ? "Refreshing..." : "Refresh Users"}
                  </Button>
                </div>
              </motion.div>
            ) : null}

            {/* Layer 2: Card Stack (Only if current user exists) */}
            {!loading && currentUser && (
              <div
                className="relative w-full overflow-visible"
                style={{
                  zIndex: 20,
                  aspectRatio: '3/4',
                  // When shown as the V2 swipe overlay there is no bottom nav,
                  // so subtract ~160px instead of ~280px for a taller card.
                  maxHeight: onReturnToV2
                    ? 'min(calc(92dvh - 160px), 640px)'
                    : 'min(calc(92dvh - 280px), 520px)',
                }}
              >
                <div className="relative w-full h-full z-10">
                  {users.slice(currentIndex, currentIndex + 2).map((user, index) => (
                    <SwipeableCard
                      ref={index === 0 ? activeCardRef : null}
                      key={String(user.id)}
                      user={user}
                      index={index}
                      totalUsers={users.length}
                      onSwipe={index === 0 ? (direction) => {
                         if (direction === 'left') {
                            handlePass();
                         } else {
                            handleLike();
                         }
                      } : () => {}}
                      onPress={() => {
                        setViewingUser(user);
                      }}
                      onSeeMore={() => {
                        setShowUserDetail(true);
                      }}
                      theme={theme}
                      formattedAge={calculateAge(user.dateOfBirth) ?? undefined}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>


          {/* Action Buttons - Only show when there is a user and NOT loading */}
          {!loading && currentUser && (
            <div className="mt-4 flex justify-between items-center px-2" style={{ position: 'relative', zIndex: 200 }}>
              {/* Left group: Tap hint + X (pass) button */}
              <div className="flex items-center gap-3">
                <motion.div
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="flex items-center"
                >
                  <img src={(theme === 'light' ? tapBlack : tapIcon) as any} alt="Tap" style={{ width: scaleSize(56), height: scaleSize(56) }} className="object-contain opacity-80" />
                </motion.div>
                <motion.button
                  data-tour="guide-swipe-reject"
                  whileTap={{ scale: 0.9 }}
                  onClick={handlePass}
                  className="rounded-full bg-white dark:bg-[#1a1a1a] flex items-center justify-center hover:opacity-90 transition-all shadow-lg button-shadow"
                  style={{ position: 'relative', zIndex: 200, width: scaleSize(60), height: scaleSize(60), aspectRatio: '1/1' }}
                >
                  <X
                    style={{ width: scaleSize(28), height: scaleSize(28), stroke: `url(#${theme === 'dark' ? 'brand-gradient-blue-svg' : 'brand-gradient-svg'})` }}
                  />
                </motion.button>
              </div>

              {/* Center: Return-to-V2 Zap button — only shown when V1 is an overlay from V2 */}
              {onReturnToV2 && (
                <div className="flex flex-col items-center" style={{ position: 'relative', zIndex: 200 }}>
                  <motion.button
                    data-tour="guide-swipe-back"
                    whileTap={{ scale: 0.9 }}
                    onClick={onReturnToV2}
                    aria-label="Return to Discover feed"
                    className="rounded-full flex items-center justify-center text-white shadow-lg button-shadow"
                    style={{
                      width: scaleSize(52),
                      height: scaleSize(52),
                      aspectRatio: '1/1',
                      background: 'linear-gradient(135deg, #4478ea 0%, #2d1ef1 100%)',
                      boxShadow: '0 4px 16px rgba(68,120,234,0.50)',
                    }}
                  >
                    <Zap style={{ width: scaleSize(24), height: scaleSize(24) }} />
                  </motion.button>
                  <span
                    className="font-bold uppercase tracking-wide mt-1 text-white"
                    style={{
                      fontSize: scaleSize(10),
                      letterSpacing: '0.06em',
                      textShadow: '0 1px 3px rgba(0,0,0,0.6)',
                    }}
                  >
                    Go Back
                  </span>
                </div>
              )}

              {/* Right group: UserPlus (like) button + Swipe hint */}
              <div className="flex items-center gap-3">
                <motion.button
                  data-tour="guide-swipe-add"
                  whileTap={{ scale: 0.9 }}
                  onClick={handleLike}
                  className="rounded-full bg-brand-gradient flex items-center justify-center shadow-lg hover:shadow-xl transition-shadow button-shadow cursor-pointer touch-manipulation"
                  style={{ position: 'relative', zIndex: 200, width: scaleSize(72), height: scaleSize(72), aspectRatio: '1/1' }}
                >
                  <UserPlus style={{ width: scaleSize(32), height: scaleSize(32) }} className="text-white" />
                </motion.button>
                <motion.div
                  initial={{ opacity: 0, x: 10 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="flex items-center"
                >
                  <img src={(theme === 'light' ? swipeBlack : swipeIcon) as any} alt="Swipe" style={{ width: scaleSize(56), height: scaleSize(56) }} className="object-contain opacity-80" />
                </motion.div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* User Detail Modal */}
      {showUserDetail && (
        <UserDetailModal
          isOpen={showUserDetail}
          onClose={() => setShowUserDetail(false)}
          user={{
            ...currentUser,
            id: currentUser.id, // 🔒 PATCH: Avoid Number() conversion which causes NaN for string UIDs
            age: calculateAge(currentUser.dateOfBirth) ?? undefined,
          }}
        />
      )}

      {/* Discover Filter Modal */}
      <DiscoverFilterModal
        isOpen={showFilterModal}
        onClose={() => setShowFilterModal(false)}
        onApplyFilters={(newFilters) => {
          setFilters(newFilters);
          // 💾 Persist age range — it's a personal preference, not a session filter
          SafeStorage.setItem('discover_age_range', JSON.stringify(newFilters.ageRange));
          setUsers([]);
          setCurrentIndex(0);
          swipedUserIds.current.clear();
          SafeStorage.removeItem('discover_last_user_id');
          setIsRefreshMode(true);
          setIsManualRefreshing(true);
          setRefreshKey(prev => prev + 1);
        }}
        currentFilters={filters}
      />

      {/* Portfolio Image Viewer Modal */}
      <ProfilePictureModal
        isOpen={viewingUser !== null}
        onClose={() => setViewingUser(null)}
        imageUrl={viewingUser?.avatar || viewingUser?.image || ''}
        images={viewingUser?.portfolio || []}
        videoUrls={viewingUser?.portfolioVideos || []}
        photoMetadata={viewingUser?.photoMetadata}
        userName={viewingUser?.name || ''}
      />

      {/* Match Celebration Modal - MOVED TO APP.TSX FOR GLOBAL STATE CONSISTENCY */}

      {/* Insufficient Funds Modal */}
      <InsufficientFundsModal
        isOpen={showInsufficientFundsModal}
        onClose={() => setShowInsufficientFundsModal(false)}
        onGoToMarket={navigateToMarket}
        requiredAmount={SWIPE_COSTS[swipeMode]}
        currentAmount={userProfile.goldBalance || 0}
        customMessage={swipeMode === 'matching'
          ? `You need 30 gold to connect with someone like this. You have ${userProfile.goldBalance || 0} gold.`
          : `You need 10 gold to swipe. You have ${userProfile.goldBalance || 0} gold.`
        }
      />
    </motion.div>
  );
}
